/**
 * Workshop record sections — the read-through views for everything a
 * workshop accumulates (artefacts, captures, decisions, actions,
 * assumptions, transcripts) plus the "carried forward from prior
 * workshops" continuity block.
 *
 * Extracted from the former `WorkshopRepository` modal so the same body can
 * render inside the global unified surface (`RepositoryOverlay`) where the
 * workshop is a filter rather than a route param. The editor-linked sections
 * delegate "open editor" / "open gallery" to callbacks so the host decides
 * how to mount those modals (the unified page mounts them locally, scoped to
 * the selected workshop).
 *
 * Local-first (IndexedDB) by default; when the BFF backend is enabled the
 * record stores read/write Azure blobs transparently (see
 * `create-workshop-record-store.ts`). Transcripts are captured *here* (paste
 * or load-from-file) — the full text is stored on the record so it's
 * full-text searchable.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  Camera,
  ClipboardCheck,
  FileText,
  Film,
  FlaskConical,
  Lightbulb,
  ListChecks,
  NotebookPen,
  Paperclip,
  Plus,
  ReceiptText,
  Scale,
  ScrollText,
  Sparkles,
  Telescope,
  Upload as UploadIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { deleteBlob, saveBlob } from "@/lib/blob-store";
import { extFromFilename, mediaKindFromMime, mimeFromExt } from "@/lib/mime-ext";
import {
  deleteWorkshopBlob,
  isWorkshopBlobSyncEnabled,
  uploadWorkshopBlob,
} from "@/lib/workshop-blob-sync";
import { useAuth } from "@/hooks/use-auth";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { FIELD_CLASS } from "@/features/workshop/modals/log-controls.utils";
import { AddButton, EmptyHint, RemoveButton } from "@/features/workshop/modals/log-rows";
import { TagFilter } from "@/components/tags/TagFilter";
import { collectTags, matchesTags } from "@/lib/tags";
import { stripNs, TAG_NS_OPPORTUNITY } from "@/lib/taxonomy";
import { useWorkshopSummaries, type WorkshopSummary, type SummarySource } from "@/stores/useWorkshopSummaries";
import { RecordLinks } from "@/features/traceability/RecordLinks";
import { useLinks, type LinkEndpoint } from "@/stores/useLinks";
import { useAiDraft, buildSummaryPrompt } from "@/features/xaes-analyst/ai-draft";
import { useAssetLocker, type Upload } from "@/stores/useAssetLocker";
import { useCaptures, type UserCapture } from "@/stores/useCaptures";
import { useDecisions, type Decision } from "@/stores/useDecisions";
import { useActions, type ActionItem } from "@/stores/useActions";
import { useAssumptions, type Assumption } from "@/stores/useAssumptions";
import { useTranscripts, type Transcript } from "@/stores/useTranscripts";
import { useFindings, type Finding } from "@/stores/useFindings";
import { useOpportunities, type Opportunity } from "@/stores/useOpportunities";
import { useRecommendations, type Recommendation } from "@/stores/useRecommendations";
import { useFacilitationNotes, type FacilitationNote } from "@/stores/useFacilitationNotes";

const EMPTY_UPLOADS: readonly Upload[] = Object.freeze([]);
const EMPTY_CAPTURES: readonly UserCapture[] = Object.freeze([]);
const EMPTY_DECISIONS: readonly Decision[] = Object.freeze([]);
const EMPTY_ACTIONS: readonly ActionItem[] = Object.freeze([]);
const EMPTY_ASSUMPTIONS: readonly Assumption[] = Object.freeze([]);
const EMPTY_TRANSCRIPTS: readonly Transcript[] = Object.freeze([]);
const EMPTY_FINDINGS: readonly Finding[] = Object.freeze([]);
const EMPTY_OPPORTUNITIES: readonly Opportunity[] = Object.freeze([]);
const EMPTY_RECOMMENDATIONS: readonly Recommendation[] = Object.freeze([]);
const EMPTY_NOTES: readonly FacilitationNote[] = Object.freeze([]);

/** Client-side cap for attached recordings, matching the server plane's
 *  100 MB limit (BFF_MAX_UPLOAD_BYTES default; nginx client_max_body_size
 *  100m). Rejecting at selection gives a clear message instead of a failed
 *  IndexedDB write plus a 413 from the blob mirror. */
const MAX_MEDIA_BYTES = 100 * 1024 * 1024;

const ROW_CARD: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "7px 10px",
  borderRadius: 8,
  background: "rgba(var(--accent-true-blue-rgb), 0.04)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
};
const ROW_TITLE: CSSProperties = {
  fontSize: "var(--fs-base)",
  fontWeight: 600,
  color: "var(--text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const ROW_SUB: CSSProperties = {
  fontSize: "var(--fs-label)",
  color: "var(--text-muted)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** Total item count across every record type for one workshop. */
export function useWorkshopRecordCount(workshopId: Workshop["id"]): number {
  const uploads = useAssetLocker((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const captures = useCaptures((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const decisions = useDecisions((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const actions = useActions((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const assumptions = useAssumptions((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const transcripts = useTranscripts((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const findings = useFindings((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const opportunities = useOpportunities((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const recommendations = useRecommendations((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  const notes = useFacilitationNotes((s) => s.entriesByWorkshop[workshopId]?.length ?? 0);
  return (
    uploads +
    captures +
    decisions +
    actions +
    assumptions +
    transcripts +
    findings +
    opportunities +
    recommendations +
    notes
  );
}

type WorkshopRecordsViewProps = {
  workshopId: Workshop["id"];
  /** Open the Workshop Log editor (decisions/actions/assumptions). */
  onEditLog?: () => void;
  /** Open the Captures gallery. */
  onOpenCaptures?: () => void;
};

/**
 * The full record body for one workshop. Hydrates the stores it reads on
 * mount (and whenever the selected workshop changes); idempotent.
 */
export function WorkshopRecordsView({
  workshopId,
  onEditLog,
  onOpenCaptures,
}: WorkshopRecordsViewProps) {
  const wsId = workshopId;
  const auth = useAuth();
  const addedBy = auth.user?.displayName ?? auth.user?.username ?? "anon";

  const uploads = useAssetLocker(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_UPLOADS as Upload[]),
  );
  const captures = useCaptures(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_CAPTURES as UserCapture[]),
  );
  const decisions = useDecisions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_DECISIONS as Decision[]),
  );
  const actions = useActions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_ACTIONS as ActionItem[]),
  );
  const assumptions = useAssumptions(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_ASSUMPTIONS as Assumption[]),
  );
  const transcripts = useTranscripts(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_TRANSCRIPTS as Transcript[]),
  );
  const findings = useFindings(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_FINDINGS as Finding[]),
  );
  const opportunities = useOpportunities(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_OPPORTUNITIES as Opportunity[]),
  );
  const recommendations = useRecommendations(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_RECOMMENDATIONS as Recommendation[]),
  );
  const notes = useFacilitationNotes(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_NOTES as FacilitationNote[]),
  );
  // CC-SUMMARIES — this workshop's editable summary + the whole map (so the
  // carried-forward block can render each prior workshop's summary).
  const summary = useWorkshopSummaries((s) => s.byWorkshop[wsId]);
  const summariesByWs = useWorkshopSummaries((s) => s.byWorkshop);
  const setSummary = useWorkshopSummaries((s) => s.set);
  const addTranscript = useTranscripts((s) => s.add);
  const updateTranscript = useTranscripts((s) => s.update);
  const removeTranscript = useTranscripts((s) => s.remove);

  // Whole-map subscriptions for the cross-workshop "carried forward"
  // section. New reference only when a bucket actually changes. Continuity
  // carries the durable *outputs* of prior workshops, not just decisions +
  // assumptions: findings/opportunities/recommendations and open actions are
  // first-class outputs the next workshop must build on (Facilitator US1).
  const decisionsByWs = useDecisions((s) => s.entriesByWorkshop);
  const assumptionsByWs = useAssumptions((s) => s.entriesByWorkshop);
  const actionsByWs = useActions((s) => s.entriesByWorkshop);
  const findingsByWs = useFindings((s) => s.entriesByWorkshop);
  const opportunitiesByWs = useOpportunities((s) => s.entriesByWorkshop);
  const recommendationsByWs = useRecommendations((s) => s.entriesByWorkshop);
  const decisionsHydrated = useDecisions((s) => s.isHydrated);
  const assumptionsHydrated = useAssumptions((s) => s.isHydrated);
  const actionsHydrated = useActions((s) => s.isHydrated);
  const findingsHydrated = useFindings((s) => s.isHydrated);
  const opportunitiesHydrated = useOpportunities((s) => s.isHydrated);
  const recommendationsHydrated = useRecommendations((s) => s.isHydrated);
  const priorsHydrated =
    decisionsHydrated &&
    assumptionsHydrated &&
    actionsHydrated &&
    findingsHydrated &&
    opportunitiesHydrated &&
    recommendationsHydrated;

  // Surface a single "couldn't reach the server" banner if any record store's
  // BFF hydrate failed entirely (it still shows the local cache). Mirrors the
  // error affordance the adjacent DocumentsPanel already has. Each store hook
  // is read unconditionally (no `||` short-circuit between hook calls) so the
  // hook order stays stable across renders once a store reports an error.
  const decisionsLoadError = useDecisions((s) => s.loadError);
  const actionsLoadError = useActions((s) => s.loadError);
  const assumptionsLoadError = useAssumptions((s) => s.loadError);
  const transcriptsLoadError = useTranscripts((s) => s.loadError);
  const recordsLoadError =
    decisionsLoadError ||
    actionsLoadError ||
    assumptionsLoadError ||
    transcriptsLoadError;

  // Hydrate everything this view reads on mount. Idempotent (guarded by
  // each store's isHydrated), so this is safe across StrictMode + remounts.
  useEffect(() => {
    void useAssetLocker.getState().hydrate();
    void useCaptures.getState().hydrate();
    void useDecisions.getState().hydrate();
    void useActions.getState().hydrate();
    void useAssumptions.getState().hydrate();
    void useTranscripts.getState().hydrate();
    void useFindings.getState().hydrate();
    void useOpportunities.getState().hydrate();
    void useRecommendations.getState().hydrate();
    void useFacilitationNotes.getState().hydrate();
    void useWorkshopSummaries.getState().hydrate();
    void useLinks.getState().hydrate(); // CC-LINKS — so on-record chips render
  }, []);

  // CC-TAGGING — the Repository tag filter. `shown` narrows a record list to
  // the active-tag facet (AND); an empty filter is a no-op.
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const shown = <T extends { tags?: string[] }>(items: readonly T[]): T[] =>
    activeTags.length ? items.filter((i) => matchesTags(i.tags, activeTags)) : (items as T[]);
  const allTags = useMemo(
    () =>
      collectTags([
        ...uploads,
        ...captures,
        ...decisions,
        ...actions,
        ...assumptions,
        ...transcripts,
        ...findings,
        ...opportunities,
        ...recommendations,
        ...notes,
      ]),
    [uploads, captures, decisions, actions, assumptions, transcripts, findings, opportunities, recommendations, notes],
  );

  const shownUploads = shown(uploads);
  const shownCaptures = shown(captures);
  const shownDecisions = shown(decisions);
  const shownActions = shown(actions);
  const shownAssumptions = shown(assumptions);
  const shownTranscripts = shown(transcripts);
  const shownFindings = shown(findings);
  const shownOpportunities = shown(opportunities);
  const shownRecommendations = shown(recommendations);
  const shownNotes = shown(notes);

  // ── Carried forward — prior workshops' durable knowledge ──
  const currentNum = WORKSHOPS.find((w) => w.id === wsId)?.num ?? 0;
  const carriedForward = useMemo(() => {
    return WORKSHOPS.filter((w) => w.num < currentNum)
      .map((w) => ({
        ws: w,
        decisions: decisionsByWs[w.id] ?? [],
        assumptions: assumptionsByWs[w.id] ?? [],
        findings: findingsByWs[w.id] ?? [],
        opportunities: opportunitiesByWs[w.id] ?? [],
        recommendations: recommendationsByWs[w.id] ?? [],
        // Only OPEN actions carry forward — a done action isn't unfinished
        // business the next workshop needs to pick up.
        actions: (actionsByWs[w.id] ?? []).filter((a) => a.status !== "done"),
      }))
      .filter(
        (g) =>
          g.decisions.length +
            g.assumptions.length +
            g.findings.length +
            g.opportunities.length +
            g.recommendations.length +
            g.actions.length >
          0,
      );
  }, [
    currentNum,
    decisionsByWs,
    assumptionsByWs,
    findingsByWs,
    opportunitiesByWs,
    recommendationsByWs,
    actionsByWs,
  ]);

  /** Total carried-forward items across every prior workshop + type. */
  const carriedForwardCount = carriedForward.reduce(
    (n, g) =>
      n +
      g.decisions.length +
      g.assumptions.length +
      g.findings.length +
      g.opportunities.length +
      g.recommendations.length +
      g.actions.length,
    0,
  );

  // ── Transcript capture form ──
  const [tTitle, setTTitle] = useState("");
  const [tBody, setTBody] = useState("");
  const [tMediaError, setTMediaError] = useState<string | null>(null);

  /**
   * Persist one transcript record. Called from three places, all of
   * which save IMMEDIATELY — UAT round 2 found the stage-then-ADD flow
   * confusing:
   *   - SAVE TRANSCRIPT button → pasted text only.
   *   - LOAD FILE → the file's text, the moment it's picked.
   *   - ATTACH MEDIA → the recording, the moment it's picked, paired
   *     with any text already in the box (for synced captions).
   * Media-only saves are valid — the cue parser treats an empty body as
   * zero cues, so playback renders the media player un-synced.
   */
  async function saveTranscript(
    rawBody: string,
    file: File | null,
    titleFallback?: string,
  ): Promise<void> {
    const body = rawBody.trim();
    if (!body && !file) return;
    const id = crypto.randomUUID();
    const base: Transcript = {
      id,
      workshopId: wsId,
      title:
        tTitle.trim() || titleFallback || file?.name || "Untitled transcript",
      body,
      addedAt: Date.now(),
      addedBy,
    };

    if (file) {
      const mime = file.type || mimeFromExt(extFromFilename(file.name));
      const kind = mediaKindFromMime(mime);
      if (!kind) {
        setTMediaError("That file isn't a supported audio or video type.");
        return;
      }
      const blobKey = `tmedia:${wsId}:${id}`;
      try {
        // Local-first: persist the bytes to IndexedDB so playback works offline.
        await saveBlob(blobKey, file);
      } catch {
        // Abort rather than save a record that points at a missing blob; the
        // form stays populated so the user can drop the media or retry smaller.
        setTMediaError(
          "Couldn't save the media file (it may be too large). The transcript was not added — remove the media or try a smaller file.",
        );
        return;
      }
      base.mediaBlobKey = blobKey;
      base.mediaMime = mime;
      base.mediaKind = kind;
      base.mediaName = file.name;
    }

    addTranscript(wsId, base);
    setTMediaError(null);

    // Mirror the media bytes to the BFF/Azure blob plane when a sync backend is
    // live, so playback self-heals after a cache wipe and works cross-device.
    // Optimistic: the record is already added and the local blob plays either
    // way, but a rejected mirror (e.g. the server's upload cap) must be
    // SURFACED, not swallowed — a silent failure left testers with recordings
    // that quietly never synced (UAT round 1).
    if (file && base.mediaBlobKey && isWorkshopBlobSyncEnabled()) {
      const ext = extFromFilename(file.name) || (base.mediaKind === "video" ? "mp4" : "mp3");
      void uploadWorkshopBlob("transcripts", wsId, `${id}.${ext}`, file)
        .then((synced) =>
          updateTranscript(wsId, id, { mediaPath: synced.path, mediaSha: synced.sha }),
        )
        .catch((err: unknown) => {
          const detail = err instanceof Error && err.message ? ` (${err.message})` : "";
          setTMediaError(
            `The recording was saved on this device but couldn't be uploaded to the server${detail}. It will play here, but not on other devices.`,
          );
        });
    }

    setTTitle("");
    setTBody("");
  }

  function handleRemoveTranscript(t: Transcript): void {
    if (t.mediaBlobKey) void deleteBlob(t.mediaBlobKey);
    if (t.mediaPath && isWorkshopBlobSyncEnabled()) {
      void deleteWorkshopBlob(t.mediaPath, t.mediaSha).catch(() => {
        /* swallow — optimistic; orphaned blob is moppable later */
      });
    }
    removeTranscript(wsId, t.id);
  }

  async function loadTranscriptFile(file: File): Promise<void> {
    const text = await file.text();
    // Saves straight away — no separate ADD step (UAT round 2).
    await saveTranscript(text, null, file.name);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {recordsLoadError && (
        <p
          role="alert"
          style={{
            fontSize: "var(--fs-label)",
            color: "var(--accent-gold)",
            background: "rgba(var(--accent-gold-rgb), 0.08)",
            border: "1px solid rgba(var(--accent-gold-rgb), 0.25)",
            borderRadius: 8,
            padding: "6px 10px",
          }}
        >
          {recordsLoadError}
        </p>
      )}

      {/* ── Summary (CC-SUMMARIES + AI-SUMMARIES) — editable, one per ws ── */}
      <WorkshopSummaryEditor
        workshopId={wsId}
        summary={summary}
        onSave={(body, source) =>
          setSummary({
            workshopId: wsId,
            body,
            updatedAt: Date.now(),
            updatedBy: addedBy,
            source,
          })
        }
      />

      {/* ── Tag filter (CC-TAGGING) — narrows the record sections below ── */}
      {allTags.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span
            style={{
              fontSize: "var(--fs-micro)",
              color: "var(--text-muted)",
              letterSpacing: "1px",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            Filter by tag
          </span>
          <TagFilter
            allTags={allTags}
            selected={activeTags}
            onChange={setActiveTags}
            workshopId={wsId}
            formatLabel={(t) => stripNs(TAG_NS_OPPORTUNITY, t)}
          />
        </div>
      )}

      <Section
        icon={Paperclip}
        label="Artefacts"
        count={shownUploads.length}
        emptyText="No files uploaded yet — add them in the Asset Locker."
      >
        {shownUploads.map((u) => (
          <ReadRow
            key={u.id}
            title={u.label}
            subtitle={u.addedBy}
            endpoint={{ kind: "upload", workshopId: wsId, id: u.id }}
          />
        ))}
      </Section>

      <Section
        icon={Camera}
        label="Captures"
        count={shownCaptures.length}
        emptyText="No captures yet."
        onOpen={onOpenCaptures}
        openLabel="Open gallery"
      >
        {shownCaptures.map((c) => (
          <ReadRow
            key={c.id}
            title={c.title}
            subtitle={c.activity || c.caption}
            endpoint={{ kind: "capture", workshopId: wsId, id: c.id }}
          />
        ))}
      </Section>

      <Section
        icon={Scale}
        label="Decisions"
        count={shownDecisions.length}
        emptyText="No decisions logged yet."
        onOpen={onEditLog}
        openLabel="Edit in Workshop Log"
        alwaysShowOpen
      >
        {shownDecisions.map((d) => (
          <ReadRow
            key={d.id}
            title={d.title}
            subtitle={`${d.status}${d.rationale ? ` · ${d.rationale}` : ""}`}
            endpoint={{ kind: "decision", workshopId: wsId, id: d.id }}
          />
        ))}
      </Section>

      <Section
        icon={ListChecks}
        label="Actions"
        count={shownActions.length}
        emptyText="No actions captured yet."
        onOpen={onEditLog}
        openLabel="Edit in Workshop Log"
        alwaysShowOpen
      >
        {shownActions.map((a) => (
          <ReadRow
            key={a.id}
            title={a.title}
            subtitle={`${a.status}${a.owner ? ` · ${a.owner}` : ""}${a.due ? ` · due ${a.due}` : ""}`}
            endpoint={{ kind: "action", workshopId: wsId, id: a.id }}
          />
        ))}
      </Section>

      <Section
        icon={FlaskConical}
        label="Assumptions"
        count={shownAssumptions.length}
        emptyText="No assumptions logged yet."
        onOpen={onEditLog}
        openLabel="Edit in Workshop Log"
        alwaysShowOpen
      >
        {shownAssumptions.map((a) => (
          <ReadRow
            key={a.id}
            title={a.statement}
            subtitle={`${a.status}${a.basis ? ` · ${a.basis}` : ""}`}
            endpoint={{ kind: "assumption", workshopId: wsId, id: a.id }}
          />
        ))}
      </Section>

      <Section
        icon={Telescope}
        label="Findings"
        count={shownFindings.length}
        emptyText="No findings logged yet."
        onOpen={onEditLog}
        openLabel="Edit in Workshop Log"
        alwaysShowOpen
      >
        {shownFindings.map((f) => (
          <ReadRow
            key={f.id}
            title={f.title}
            subtitle={`${f.severity} · ${f.domain} · ${f.status}${f.owner ? ` · ${f.owner}` : ""}`}
            endpoint={{ kind: "finding", workshopId: wsId, id: f.id }}
          />
        ))}
      </Section>

      <Section
        icon={Lightbulb}
        label="Opportunities"
        count={shownOpportunities.length}
        emptyText="No opportunities identified yet."
        onOpen={onEditLog}
        openLabel="Edit in Workshop Log"
        alwaysShowOpen
      >
        {shownOpportunities.map((o) => (
          <ReadRow
            key={o.id}
            title={o.title}
            subtitle={`${o.status}${o.detail ? ` · ${o.detail}` : ""}`}
            endpoint={{ kind: "opportunity", workshopId: wsId, id: o.id }}
          />
        ))}
      </Section>

      <Section
        icon={ReceiptText}
        label="Recommendations"
        count={shownRecommendations.length}
        emptyText="No recommendations drafted yet."
        onOpen={onEditLog}
        openLabel="Edit in Workshop Log"
        alwaysShowOpen
      >
        {shownRecommendations.map((r) => (
          <ReadRow
            key={r.id}
            title={r.title}
            subtitle={`${r.status}${r.body ? ` · ${r.body}` : ""}`}
            endpoint={{ kind: "recommendation", workshopId: wsId, id: r.id }}
          />
        ))}
      </Section>

      <Section
        icon={NotebookPen}
        label="Facilitation notes"
        count={shownNotes.length}
        emptyText="No facilitation notes yet."
        onOpen={onEditLog}
        openLabel="Edit in Workshop Log"
        alwaysShowOpen
      >
        {shownNotes.map((n) => (
          <ReadRow
            key={n.id}
            title={n.body}
            subtitle={n.addedBy}
            endpoint={{ kind: "note", workshopId: wsId, id: n.id }}
          />
        ))}
      </Section>

      {/* ── Transcripts (captured here) ── */}
      <section>
        <SectionHead icon={FileText} label="Transcripts" count={shownTranscripts.length} />
        <div className="flex flex-col gap-2 pt-2">
          <input
            aria-label="Transcript title"
            type="text"
            value={tTitle}
            onChange={(e) => setTTitle(e.target.value)}
            placeholder="Transcript title — e.g. Activity 2 full transcript"
            className={FIELD_CLASS}
          />
          <textarea
            aria-label="Transcript text"
            value={tBody}
            onChange={(e) => setTBody(e.target.value)}
            placeholder="Paste the transcript text here…"
            rows={4}
            className={cn(FIELD_CLASS, "resize-y")}
          />
          <div className="flex flex-wrap items-center gap-2">
            <label
              className="modal-close-btn inline-flex items-center gap-1.5 focus-within:outline focus-within:outline-1 focus-within:outline-[var(--accent-true-blue)]"
              style={{ flex: "none", cursor: "pointer" }}
            >
              <UploadIcon aria-hidden style={{ width: 12, height: 12 }} strokeWidth={2.5} />
              LOAD FILE
              <input
                type="file"
                accept=".txt,.md,.vtt,.srt,text/plain,text/markdown"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void loadTranscriptFile(f);
                }}
              />
            </label>
            <label
              className="modal-close-btn inline-flex items-center gap-1.5 focus-within:outline focus-within:outline-1 focus-within:outline-[var(--accent-true-blue)]"
              style={{ flex: "none", cursor: "pointer" }}
              title="Attach an audio or video recording for synced playback"
            >
              <Film aria-hidden style={{ width: 12, height: 12 }} strokeWidth={2.5} />
              ATTACH MEDIA
              <input
                type="file"
                accept="audio/*,video/*"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  setTMediaError(null);
                  if (!f) return;
                  if (f.size > MAX_MEDIA_BYTES) {
                    setTMediaError(
                      `That recording is ${Math.round(f.size / 1024 / 1024)} MB — the limit is ${MAX_MEDIA_BYTES / 1024 / 1024} MB. Try a compressed export or an audio-only version.`,
                    );
                    return;
                  }
                  // Saves straight away, paired with any text already
                  // in the box (synced captions) — no ADD step.
                  void saveTranscript(tBody, f);
                }}
              />
            </label>
            {tBody.trim() && (
              <button
                type="button"
                onClick={() => void saveTranscript(tBody, null)}
                className="modal-close-btn inline-flex items-center gap-1.5"
                style={{ flex: "none" }}
              >
                <Plus aria-hidden style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                SAVE TRANSCRIPT
              </button>
            )}
            <span
              style={{
                fontSize: "var(--fs-micro)",
                color: "var(--text-muted)",
                letterSpacing: "0.3px",
              }}
            >
              Files save as soon as you pick them.
            </span>
          </div>
          {tMediaError && (
            <p role="alert" style={{ fontSize: "var(--fs-micro)", color: "var(--accent-red)" }}>
              {tMediaError}
            </p>
          )}
        </div>
        <ul className="flex flex-col gap-1.5 pt-3">
          {shownTranscripts.length === 0 && <EmptyHint text="No transcripts captured yet." />}
          {shownTranscripts.map((t) => (
            <li key={t.id}>
              <TranscriptRow transcript={t} onRemove={() => handleRemoveTranscript(t)} />
            </li>
          ))}
        </ul>
      </section>

      {/* ── Carried forward from prior workshops (continuity) ── */}
      <section>
        <SectionHead
          icon={ClipboardCheck}
          label="Carried forward from prior workshops"
          count={carriedForwardCount}
        />
        <div className="flex flex-col gap-3 pt-2">
          {currentNum <= 1 && (
            <EmptyHint text="This is the first workshop — nothing precedes it." />
          )}
          {currentNum > 1 &&
            carriedForward.length === 0 &&
            (priorsHydrated ? (
              <EmptyHint text="No decisions or assumptions captured in earlier workshops yet." />
            ) : (
              <EmptyHint text="Loading prior workshops…" />
            ))}
          {carriedForward.map((g) => (
            <div key={g.ws.id}>
              <div
                style={{
                  fontSize: "var(--fs-label)",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  color: "var(--accent-sky)",
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                {g.ws.longTitle}
              </div>
              {summariesByWs[g.ws.id]?.body && (
                <p
                  style={{
                    fontSize: "var(--fs-label)",
                    color: "var(--text-secondary)",
                    fontStyle: "italic",
                    margin: "0 0 6px",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {summariesByWs[g.ws.id]!.body}
                </p>
              )}
              <ul className="flex flex-col gap-1.5">
                {g.decisions.map((d) => (
                  <ReadRow key={d.id} title={d.title} subtitle={`Decision · ${d.status}`} />
                ))}
                {g.assumptions.map((a) => (
                  <ReadRow
                    key={a.id}
                    title={a.statement}
                    subtitle={`Assumption · ${a.status}`}
                  />
                ))}
                {g.findings.map((f) => (
                  <ReadRow
                    key={f.id}
                    title={f.title}
                    subtitle={`Finding · ${f.severity} · ${f.domain}`}
                  />
                ))}
                {g.opportunities.map((o) => (
                  <ReadRow key={o.id} title={o.title} subtitle={`Opportunity · ${o.status}`} />
                ))}
                {g.recommendations.map((r) => (
                  <ReadRow key={r.id} title={r.title} subtitle={`Recommendation · ${r.status}`} />
                ))}
                {g.actions.map((a) => (
                  <ReadRow
                    key={a.id}
                    title={a.title}
                    subtitle={`Open action · ${a.owner || "unassigned"}${a.due ? ` · due ${a.due}` : ""}`}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ── helpers (shared by the view + any host) ── */

/** Editable previous-workshop summary (CC-SUMMARIES). Draft is local until
 *  Save; shows when it was last updated + by whom. AI-SUMMARIES adds an
 *  opt-in "Draft with AI" button that streams a grounded draft into the
 *  box — human-in-the-loop (the user edits + Saves), source flagged `ai`,
 *  never auto-committed; keyless it streams the explicit mock banner. */
function WorkshopSummaryEditor({
  workshopId,
  summary,
  onSave,
}: {
  workshopId: Workshop["id"];
  summary: WorkshopSummary | undefined;
  onSave: (body: string, source: SummarySource) => void;
}) {
  const [draft, setDraft] = useState(summary?.body ?? "");
  const [aiSourced, setAiSourced] = useState(summary?.source === "ai");
  useEffect(() => {
    setDraft(summary?.body ?? "");
    setAiSourced(summary?.source === "ai");
  }, [summary?.body, summary?.source]);

  const ai = useAiDraft();
  // Stream the AI draft into the editable box as it arrives.
  useEffect(() => {
    if (ai.status === "streaming" || (ai.status === "idle" && ai.text)) setDraft(ai.text);
  }, [ai.text, ai.status]);

  const dirty = draft.trim() !== (summary?.body ?? "");
  const streaming = ai.status === "streaming";

  function draftWithAi(): void {
    void (async () => {
      const { system, prompt } = await buildSummaryPrompt(workshopId);
      setAiSourced(true);
      await ai.run(system, prompt);
    })();
  }

  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <SectionHead icon={ScrollText} label="Summary" count={summary ? 1 : 0} />
        {summary && (
          <span style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)" }}>
            {summary.source === "ai" ? "AI draft · " : ""}
            {summary.updatedBy || "—"}
          </span>
        )}
        {!ai.configured && (
          <span style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)" }}>
            (AI draft runs in mock mode — no key set)
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2 pt-2">
        <textarea
          aria-label="Workshop summary"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Summarise where this workshop left things — carried forward into the next."
          rows={2}
          className={cn(FIELD_CLASS, "resize-y")}
        />
        {ai.error && (
          <p style={{ fontSize: "var(--fs-micro)", color: "var(--accent-red)" }}>{ai.error}</p>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            onClick={draftWithAi}
            disabled={streaming}
            className="modal-close-btn inline-flex items-center gap-1.5"
            title="Draft a summary with AI (editable before you save)"
            style={{ flex: "none", opacity: streaming ? 0.5 : 1, cursor: streaming ? "default" : "pointer" }}
          >
            <Sparkles aria-hidden style={{ width: 12, height: 12 }} />
            {streaming ? "DRAFTING…" : "DRAFT WITH AI"}
          </button>
          <AddButton
            onClick={() => onSave(draft.trim(), aiSourced ? "ai" : "manual")}
            disabled={!dirty || streaming}
            label="SAVE SUMMARY"
          />
        </div>
      </div>
    </section>
  );
}

export function SectionHead({
  icon: Icon,
  label,
  count,
}: {
  icon: typeof Camera;
  label: string;
  count: number;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon aria-hidden style={{ width: 14, height: 14, color: "var(--accent-true-blue)" }} />
      <span
        style={{
          fontSize: "var(--fs-label)",
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          color: "var(--text-secondary)",
          fontWeight: 700,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)", fontWeight: 600 }}>
        ({count})
      </span>
    </div>
  );
}

export function Section({
  icon,
  label,
  count,
  emptyText,
  onOpen,
  openLabel,
  alwaysShowOpen,
  children,
}: {
  icon: typeof Camera;
  label: string;
  count: number;
  emptyText: string;
  onOpen?: () => void;
  openLabel?: string;
  // Editor-linked sections (Decisions/Actions/Assumptions) keep their
  // "Edit in Workshop Log" shortcut visible even when empty — otherwise
  // an empty section hides the only path to create the first record.
  // Read-through sections (e.g. Captures) keep the count>0 gate.
  alwaysShowOpen?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <SectionHead icon={icon} label={label} count={count} />
        {onOpen && (alwaysShowOpen || count > 0) && (
          <button
            type="button"
            onClick={onOpen}
            className="modal-close-btn"
            style={{ flex: "none" }}
          >
            {openLabel ?? "Open"}
          </button>
        )}
      </div>
      <ul className="flex flex-col gap-1.5 pt-2">
        {count === 0 ? <EmptyHint text={emptyText} /> : children}
      </ul>
    </section>
  );
}

export function ReadRow({
  title,
  subtitle,
  endpoint,
}: {
  title: string;
  subtitle?: string;
  /** When set, render the CC-LINKS inline link affordance (chips + picker)
   *  for this record. Omitted on non-linkable rows (transcripts) and on the
   *  static report/analysis panes that reuse this atom. */
  endpoint?: LinkEndpoint;
}) {
  return (
    <li style={{ ...ROW_CARD, alignItems: endpoint ? "flex-start" : "center" }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={ROW_TITLE}>{title}</div>
        {subtitle && <div style={ROW_SUB}>{subtitle}</div>}
        {endpoint && <RecordLinks endpoint={endpoint} />}
      </div>
    </li>
  );
}

export function TranscriptRow({
  transcript,
  onRemove,
}: {
  transcript: Transcript;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={ROW_CARD}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? `Collapse transcript: ${transcript.title}`
              : `Expand transcript: ${transcript.title}`
          }
          style={{
            ...ROW_TITLE,
            background: "transparent",
            border: 0,
            padding: 0,
            cursor: "pointer",
            textAlign: "left",
            width: "100%",
            whiteSpace: expanded ? "normal" : "nowrap",
          }}
          title={expanded ? "Collapse" : "Expand"}
        >
          {transcript.title}
          {transcript.mediaKind && (
            <Film
              aria-label={`Has attached ${transcript.mediaKind}`}
              style={{
                width: 11,
                height: 11,
                marginLeft: 6,
                marginBottom: -1,
                display: "inline",
                color: "var(--accent-sky)",
              }}
            />
          )}
        </button>
        {expanded ? (
          <pre
            style={{
              marginTop: 6,
              maxHeight: 220,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "Inter, sans-serif",
              fontSize: "var(--fs-label)",
              lineHeight: 1.6,
              color: "var(--text-secondary)",
            }}
          >
            {transcript.body}
          </pre>
        ) : (
          <div style={ROW_SUB}>
            {transcript.body.length > 110
              ? `${transcript.body.slice(0, 110).trim()}…`
              : transcript.body}
          </div>
        )}
      </div>
      <RemoveButton
        label={`Remove transcript: ${transcript.title}`}
        onClick={onRemove}
        style={{ alignSelf: "flex-start" }}
      />
    </div>
  );
}
